import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import usePageMeta from '../hooks/usePageMeta';
import { CONTACT } from '../content/site';

const LABEL = 'font-mono text-[10px] uppercase tracking-[0.25em] text-accentText';
const BODY = 'text-sm leading-relaxed text-dim sm:text-base';
const H2 = 'font-display text-xl font-bold uppercase tracking-tight text-white';

const SECTIONS = [
  {
    title: 'What we’re doing',
    bullets: [
      'Reviewing our pages for accessibility, including company and product pages',
      'Improving image descriptions, video captions, color contrast and keyboard navigation',
      'Considering accessibility when we add new content',
      'Listening to customer feedback and using it to make improvements',
    ],
  },
  {
    title: 'Need help or want to report a problem?',
    paragraphs: [
      'Some parts of our site may not work as well as they should, including features provided by third parties. If you have trouble using any part of the site, or need information in another format, please contact us:',
    ],
  },
];

export default function AccessibilityStatement() {
  usePageMeta(
    'Accessibility | RangeSafe Commercial Solutions',
    'RangeSafe is working to make its website easier to use for everyone, including people with disabilities, aligned with WCAG 2.1 Level AA.'
  );
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="Legal"
          lines={['Accessibility']}
          lead="RangeSafe LLC wants everyone to be able to use our website, including people with disabilities. We are working to make our site easier to use and to align it with the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA."
        />
        <p className={`mt-6 ${LABEL}`} data-testid="accessibility-updated">
          RangeSafe Commercial Website Accessibility · Last updated: November 4, 2026
        </p>
      </header>

      <div className="mx-auto max-w-shell space-y-12 px-5 pb-24 sm:px-8">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={Math.min(i * 0.03, 0.15)}>
            <section className="max-w-3xl" data-testid={`accessibility-section-${i + 1}`}>
              <h2 className={H2}>{s.title}</h2>
              {s.paragraphs &&
                s.paragraphs.map((p) => (
                  <p key={p.slice(0, 40)} className={`mt-4 ${BODY}`}>
                    {p}
                  </p>
                ))}
              {s.bullets && (
                <ul className="mt-4 space-y-3">
                  {s.bullets.map((b) => (
                    <li key={b.slice(0, 40)} className="flex gap-4">
                      <span className="mt-2 h-1 w-1 shrink-0 bg-accent" aria-hidden="true" />
                      <span className={BODY}>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </Reveal>
        ))}

        <Reveal>
          <section className="border border-line bg-panel/60 p-8 sm:p-10" data-testid="accessibility-contact">
            <p className={LABEL}>Contact Us</p>
            <div className="mt-4 space-y-1 text-sm leading-relaxed text-dim">
              <p className="text-white">RangeSafe, LLC</p>
              <p>Email: {CONTACT.generalEmail}</p>
              <p>Phone: {CONTACT.phone}</p>
            </div>
            <p className={`mt-4 max-w-2xl ${BODY}`}>
              Please tell us the page you were on and the problem you ran into. We’ll respond within five
              business days and work with you to provide the information or complete your order another way.
            </p>
          </section>
        </Reveal>
      </div>
    </main>
  );
}
